import { timerActionTypes } from './Timer.actionTypes';

const initialState = {
  // Timer state
  isPlaying: false,
  timer: 0,
  loading: false,
  error: null,

  // Timer session data
  timerSession: {
    projectId: '',
    projectName: '', // Add project name field
    taskName: '',
    taskDetails: '',
    companyId: '',
    taskId: '',
    startTime: null,
    isActive: false,
    source: 'web',
  },

  // Last timelog entry
  lastTimelogEntry: null,

  // Timer validation state
  validationError: null,

  // Success messages
  successMessage: null,

  // Punch in/out state
  punchStatus: {
    isPunchedIn: false,
    lastPunchinoutStatus: 'punchout',
    currentPunchSession: null,
    loading: false,
    error: null,
    successMessage: null,
    // NEW: Separate daily punch status and active session data
    dailyPunchStatus: {
      isPunchedIn: false,
      lastPunchinoutStatus: 'punchout',
      currentPunchSession: null,
    },
    activeSession: {
      exists: false,
      data: null,
    },
  },

  // Project and Task data
  projects: [],
  tasks: [],
  selectedProject: null,
  projectsLoading: false,
  tasksLoading: false,
  projectsError: null,
  tasksError: null,
};

const timerReducer = (state = initialState, action) => {
  // Debug all timer actions to see what's happening
  if (action.type.startsWith('timer/')) {
    console.log('WebTimeTracking : Timer Reducer called with:', action.type);
    if (action.type.includes('PUNCH_STATUS')) {
      console.log(
        'WebTimeTracking : Punch status action payload:',
        action.payload
      );
      console.log(
        'WebTimeTracking : Current state.punchStatus:',
        state.punchStatus
      );
    }
  }

  switch (action.type) {
    // Start Timer Cases
    case timerActionTypes.START_TIMER_PENDING:
      return {
        ...state,
        loading: true,
        error: null,
        validationError: null,
        successMessage: null,
      };

    case timerActionTypes.START_TIMER_SUCCESS:
      return {
        ...state,
        loading: false,
        isPlaying: true,
        timerSession: action.payload.sessionData,
        error: null,
        validationError: null,
        successMessage: 'Timer started successfully',
      };

    case timerActionTypes.START_TIMER_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        validationError: action.payload.error,
        isPlaying: false,
      };

    // Stop Timer Cases
    case timerActionTypes.STOP_TIMER_PENDING:
      return {
        ...state,
        loading: true,
        error: null,
        validationError: null,
        successMessage: null,
      };

    case timerActionTypes.STOP_TIMER_SUCCESS:
      return {
        ...state,
        loading: false,
        isPlaying: false,
        timer: 0,
        timerSession: {
          projectId: '',
          projectName: '',
          taskName: '',
          taskDetails: '',
          companyId: action.payload.timelogEntry
            ? Object.values(action.payload.timelogEntry)[0].companyID
            : '',
          taskId: '',
          startTime: null,
          isActive: false,
          source: 'web',
        },
        lastTimelogEntry: action.payload.timelogEntry,
        error: null,
        validationError: null,
        successMessage: 'Timer stopped and timelog saved successfully',
      };

    case timerActionTypes.STOP_TIMER_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        validationError: action.payload.error,
      };

    // Resume Timer Cases
    case timerActionTypes.RESUME_TIMER_PENDING:
      return {
        ...state,
        loading: true,
        error: null,
        validationError: null,
        successMessage: null,
      };

    case timerActionTypes.RESUME_TIMER_SUCCESS:
      return {
        ...state,
        loading: false,
        isPlaying: true,
        timer: action.payload.elapsedSeconds,
        timerSession: action.payload.sessionData,
        error: null,
        validationError: null,
        successMessage: 'Timer resumed successfully',
      };

    case timerActionTypes.RESUME_TIMER_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload.silent ? null : action.payload.error, // Don't set error for silent failures
        validationError: action.payload.silent ? null : action.payload.error, // Don't set validation error for silent failures
        isPlaying: false,
      };

    // Cancel Timer Cases
    case timerActionTypes.CANCEL_TIMER_PENDING:
      return {
        ...state,
        loading: true,
        error: null,
        validationError: null,
        successMessage: null,
      };

    case timerActionTypes.CANCEL_TIMER_SUCCESS:
      return {
        ...state,
        loading: false,
        isPlaying: false,
        timer: 0,
        timerSession: {
          projectId: '',
          projectName: '',
          taskName: '',
          taskDetails: '',
          companyId: state.timerSession.companyId || '',
          taskId: '',
          startTime: null,
          isActive: false,
          source: 'web',
        },
        error: null,
        validationError: null,
        successMessage: 'Timer cancelled successfully',
      };

    case timerActionTypes.CANCEL_TIMER_FAILED:
      return {
        ...state,
        loading: false,
        error: action.payload.error,
        validationError: action.payload.error,
      };

    // Timer State Management
    case timerActionTypes.SET_TIMER_STATE:
      return {
        ...state,
        ...action.payload,
      };

    case timerActionTypes.UPDATE_TIMER_TIME:
      return {
        ...state,
        timer: action.payload.timer,
      };

    case timerActionTypes.SET_TIMER_SESSION:
      return {
        ...state,
        timerSession: {
          ...state.timerSession,
          ...action.payload,
        },
      };

    case timerActionTypes.CLEAR_TIMER_SESSION:
      return {
        ...state,
        timerSession: {
          projectId: '',
          projectName: '',
          taskName: '',
          taskDetails: '',
          companyId: action.payload?.companyId || '',
          taskId: '',
          startTime: null,
          isActive: false,
          source: 'web',
        },
        timer: 0,
        isPlaying: false,
        error: null,
        validationError: null,
        successMessage: null,
      };

    // Validation Cases
    case timerActionTypes.VALIDATE_TIMER_SESSION_SUCCESS:
      return {
        ...state,
        validationError: null,
        error: null,
      };

    case timerActionTypes.VALIDATE_TIMER_SESSION_FAILED:
      return {
        ...state,
        validationError: action.payload.error,
        error: action.payload.error,
      };

    // Cross-platform validation cases
    case timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_SUCCESS:
      return {
        ...state,
        error: null,
        validationError: null,
      };

    case timerActionTypes.CHECK_CROSS_PLATFORM_SESSION_FAILED:
      return {
        ...state,
        error: action.payload.error,
        validationError: action.payload.error,
      };

    // Project Cases
    case timerActionTypes.FETCH_PROJECTS_PENDING:
      return {
        ...state,
        projectsLoading: true,
        projectsError: null,
      };

    case timerActionTypes.FETCH_PROJECTS_SUCCESS:
      return {
        ...state,
        projectsLoading: false,
        projects: action.payload.projects,
        projectsError: null,
      };

    case timerActionTypes.FETCH_PROJECTS_FAILED:
      return {
        ...state,
        projectsLoading: false,
        projectsError: action.payload.error,
        projects: [],
      };

    // Task Cases
    case timerActionTypes.FETCH_TASKS_PENDING:
      return {
        ...state,
        tasksLoading: true,
        tasksError: null,
      };

    case timerActionTypes.FETCH_TASKS_SUCCESS:
      return {
        ...state,
        tasksLoading: false,
        tasks: action.payload.tasks,
        tasksError: null,
      };

    case timerActionTypes.FETCH_TASKS_FAILED:
      return {
        ...state,
        tasksLoading: false,
        tasksError: action.payload.error,
        tasks: [],
      };

    case timerActionTypes.CLEAR_TASKS:
      return {
        ...state,
        tasks: [],
        tasksError: null,
        selectedProject: null,
      };

    case timerActionTypes.SET_SELECTED_PROJECT:
      return {
        ...state,
        selectedProject: action.payload.project,
        tasks: [], // Clear tasks when project changes
        tasksError: null,
      };

    // Punch In Cases
    case timerActionTypes.PUNCH_IN_PENDING:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: true,
          error: null,
          successMessage: null,
        },
      };

    case timerActionTypes.PUNCH_IN_SUCCESS:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: false,
          isPunchedIn: true,
          lastPunchinoutStatus: 'punchin',
          currentPunchSession: action.payload.punchData,
          // NEW: Update daily punch status as well
          dailyPunchStatus: {
            ...((state.punchStatus && state.punchStatus.dailyPunchStatus) ||
              {}),
            isPunchedIn: true,
            lastPunchinoutStatus: 'punchin',
            currentPunchSession: action.payload.punchData,
          },
          error: null,
          successMessage: 'Punched in successfully',
        },
      };

    case timerActionTypes.PUNCH_IN_FAILED:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: false,
          error: action.payload.error,
          successMessage: null,
        },
      };

    // Punch Out Cases
    case timerActionTypes.PUNCH_OUT_PENDING:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: true,
          error: null,
          successMessage: null,
        },
      };

    case timerActionTypes.PUNCH_OUT_SUCCESS:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: false,
          isPunchedIn: false,
          lastPunchinoutStatus: 'punchout',
          currentPunchSession: null,
          // NEW: Update daily punch status as well
          dailyPunchStatus: {
            ...((state.punchStatus && state.punchStatus.dailyPunchStatus) ||
              {}),
            isPunchedIn: false,
            lastPunchinoutStatus: 'punchout',
            currentPunchSession: null,
          },
          error: null,
          successMessage: 'Punched out successfully',
        },
      };

    case timerActionTypes.PUNCH_OUT_FAILED:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: false,
          error: action.payload.error,
          successMessage: null,
        },
      };

    // Check Punch Status Cases
    case timerActionTypes.CHECK_PUNCH_STATUS_PENDING:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: true,
          error: null,
        },
      };

    case timerActionTypes.CHECK_PUNCH_STATUS_SUCCESS:
      const newPunchStatus = {
        ...(state.punchStatus || {}),
        loading: false,
        isPunchedIn: action.payload.isPunchedIn,
        lastPunchinoutStatus: action.payload.lastPunchinoutStatus,
        currentPunchSession: action.payload.currentPunchSession,
        // NEW: Handle the separate daily punch status and active session data
        dailyPunchStatus: action.payload.dailyPunchStatus || {
          isPunchedIn: false,
          lastPunchinoutStatus: 'punchout',
          currentPunchSession: null,
        },
        activeSession: action.payload.activeSession || {
          exists: false,
          data: null,
        },
        error: null,
      };
      console.log(
        'WebTimeTracking : CHECK_PUNCH_STATUS_SUCCESS - New punchStatus:',
        newPunchStatus
      );
      return {
        ...state,
        punchStatus: newPunchStatus,
      };

    case timerActionTypes.CHECK_PUNCH_STATUS_FAILED:
      return {
        ...state,
        punchStatus: {
          ...(state.punchStatus || {}),
          loading: false,
          error: action.payload.error,
        },
      };

    default:
      return state;
  }
};

export default timerReducer;
